import { memo, Fragment } from 'react';
import { CreditCard, Truck } from 'lucide-react';
import { VentaActividad, ProductoActividad, Miembro } from '../../lib/supabase';
import { ProduccionConMetricas } from '../../services/produccionService';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { getNombreCliente, getNombreReferido, tieneReferido } from '../../utils/clienteHelpers';

export interface GrupoPedido {
  readonly producto: ProductoActividad | undefined;
  readonly pedidos: VentaActividad[];
  readonly totalUnidades: number;
  readonly totalMonto: number;
}

interface PedidoTableProps {
  readonly pedidos: VentaActividad[];
  readonly grupos?: GrupoPedido[];
  readonly productos: ProductoActividad[];
  readonly producciones: ProduccionConMetricas[];
  readonly miembros: Miembro[];
  readonly readOnly?: boolean;
  readonly onClickRow: (pedido: VentaActividad) => void;
  readonly onRegistrarPago: (pedido: VentaActividad) => void;
  readonly onRegistrarEntrega: (pedido: VentaActividad) => void;
}

function BadgePago({ pedido }: { readonly pedido: VentaActividad }) {
  if (pedido.estado_pago === 'pagado') {
    return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 whitespace-nowrap">Pagado</span>;
  }
  if (pedido.monto_pagado > 0) {
    return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 whitespace-nowrap">Parcial</span>;
  }
  return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 whitespace-nowrap">No pagado</span>;
}

function BadgeEntrega({ pedido }: { readonly pedido: VentaActividad }) {
  if (pedido.estado_entrega === 'entregado') {
    return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 whitespace-nowrap">Entregado</span>;
  }
  if (pedido.cantidad_entregada > 0) {
    return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 whitespace-nowrap">Parcial</span>;
  }
  return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600 whitespace-nowrap">Pendiente</span>;
}

interface PedidoRowProps {
  readonly pedido: VentaActividad;
  readonly productos: ProductoActividad[];
  readonly producciones: ProduccionConMetricas[];
  readonly miembros: Miembro[];
  readonly readOnly: boolean;
  readonly showProducto: boolean;
  readonly onClickRow: (pedido: VentaActividad) => void;
  readonly onRegistrarPago: (pedido: VentaActividad) => void;
  readonly onRegistrarEntrega: (pedido: VentaActividad) => void;
}

function PedidoRow({
  pedido,
  productos,
  producciones,
  miembros,
  readOnly,
  showProducto,
  onClickRow,
  onRegistrarPago,
  onRegistrarEntrega,
}: PedidoRowProps) {
  const producto = productos.find(p => p.id === pedido.producto_id);
  const lote = pedido.produccion_id ? producciones.find(p => p.id === pedido.produccion_id) : null;
  const nombreReferido = tieneReferido(pedido) ? getNombreReferido(pedido, miembros) : null;
  const textoDespacho = pedido.direccion_despacho ? `Despacho: ${pedido.direccion_despacho}` : 'Despacho';
  const textoFormaEntrega = pedido.forma_entrega === 'despacho' ? textoDespacho : 'Retiro en sede';

  return (
    <tr
      onClick={() => onClickRow(pedido)}
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClickRow(pedido); }}
      className="hover:bg-slate-50 cursor-pointer transition-colors"
    >
      {/* Comprador */}
      <td className="px-4 py-3">
        <p className="font-semibold text-slate-900 whitespace-nowrap">
          {getNombreCliente(pedido, miembros)}
        </p>
        {nombreReferido && (
          <p className="text-xs text-slate-500 mt-0.5 whitespace-nowrap">ref. {nombreReferido}</p>
        )}
      </td>

      {/* Producto (sin agrupación) o Cantidad (agrupado) */}
      {showProducto ? (
        <td className="px-4 py-3">
          <p className="font-medium text-purple-700 whitespace-nowrap">
            {producto?.nombre_producto ?? 'Producto'} <span className="text-slate-500 font-normal">x {pedido.cantidad}</span>
          </p>
          {lote && (
            <p className="text-xs text-slate-500 mt-0.5 whitespace-nowrap">{lote.nombre}</p>
          )}
        </td>
      ) : (
        <td className="px-3 py-3 text-center">
          <span className="font-semibold text-slate-700">{pedido.cantidad}</span>
          {lote && (
            <p className="text-xs text-slate-500 mt-0.5 whitespace-nowrap">{lote.nombre}</p>
          )}
        </td>
      )}

      {/* Pago */}
      <td className="hidden md:table-cell px-4 py-3">
        <BadgePago pedido={pedido} />
        <p className="text-xs text-slate-500 mt-0.5 whitespace-nowrap">
          {formatCurrency(pedido.monto_pagado)} de {formatCurrency(pedido.monto_total)}
        </p>
      </td>

      {/* Entrega */}
      <td className="hidden md:table-cell px-4 py-3">
        <BadgeEntrega pedido={pedido} />
        <p className="text-xs text-slate-500 mt-0.5 whitespace-nowrap">
          {pedido.cantidad_entregada} de {pedido.cantidad} unid.
        </p>
      </td>

      {/* Fecha / Forma */}
      <td className="hidden md:table-cell px-4 py-3">
        {(pedido.fecha_entrega || pedido.rango_horario_retiro_despacho) && (
          <p className="text-slate-700 whitespace-nowrap">
            {pedido.fecha_entrega && formatDate(pedido.fecha_entrega)}
            {pedido.fecha_entrega && pedido.rango_horario_retiro_despacho && ' · '}
            {pedido.rango_horario_retiro_despacho}
          </p>
        )}
        <p className="text-xs text-slate-500 mt-0.5">
          {textoFormaEntrega}
        </p>
      </td>

      {/* Total */}
      <td className="px-4 py-3 text-right">
        <span className="font-bold text-blue-600 whitespace-nowrap">
          {formatCurrency(pedido.monto_total)}
        </span>
      </td>

      {/* Acciones */}
      {!readOnly && (
        <td className="hidden md:table-cell px-4 py-3">
          <div className="flex items-center gap-1">
            {pedido.estado_pago !== 'pagado' && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onRegistrarPago(pedido); }}
                className="p-1.5 rounded-lg bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors"
                title="Registrar pago"
              >
                <CreditCard className="w-4 h-4" />
              </button>
            )}
            {pedido.estado_entrega !== 'entregado' && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onRegistrarEntrega(pedido); }}
                className="p-1.5 rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors"
                title="Registrar entrega"
              >
                <Truck className="w-4 h-4" />
              </button>
            )}
          </div>
        </td>
      )}
    </tr>
  );
}

export const PedidoTable = memo<PedidoTableProps>(({
  pedidos,
  grupos,
  productos,
  producciones,
  miembros,
  readOnly = false,
  onClickRow,
  onRegistrarPago,
  onRegistrarEntrega,
}) => {
  const agrupado = grupos !== undefined;

  const rowProps = { productos, producciones, miembros, readOnly, showProducto: !agrupado, onClickRow, onRegistrarPago, onRegistrarEntrega };

  return (
    <div className="w-full overflow-x-auto rounded-xl border border-slate-200">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-200">
            <th className="text-left px-4 py-3 font-semibold text-slate-600 whitespace-nowrap">Comprador</th>
            {agrupado
              ? <th className="text-center px-3 py-3 font-semibold text-slate-600 whitespace-nowrap w-14">Cant.</th>
              : <th className="text-left px-4 py-3 font-semibold text-slate-600 whitespace-nowrap">Producto</th>
            }
            <th className="hidden md:table-cell text-left px-4 py-3 font-semibold text-slate-600 whitespace-nowrap">Pago</th>
            <th className="hidden md:table-cell text-left px-4 py-3 font-semibold text-slate-600 whitespace-nowrap">Entrega</th>
            <th className="hidden md:table-cell text-left px-4 py-3 font-semibold text-slate-600 whitespace-nowrap">Fecha / Forma</th>
            <th className="text-right px-4 py-3 font-semibold text-slate-600 whitespace-nowrap">Total</th>
            {!readOnly && <th className="hidden md:table-cell px-4 py-3" />}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {grupos === undefined ? (
            pedidos.map((pedido) => (
              <PedidoRow key={pedido.id} pedido={pedido} {...rowProps} />
            ))
          ) : (
            grupos.map((grupo) => (
              <Fragment key={grupo.producto?.id ?? '__sin_producto__'}>
                <tr className="bg-purple-50 border-y border-purple-100">
                  <td className="px-4 py-2">
                    <span className="font-semibold text-purple-800">
                      {grupo.producto?.nombre_producto ?? 'Sin producto'}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-center">
                    <span className="font-semibold text-purple-700">{grupo.totalUnidades}</span>
                    <p className="text-xs text-purple-500">unid.</p>
                  </td>
                  <td className="hidden md:table-cell px-4 py-2" />
                  <td className="hidden md:table-cell px-4 py-2" />
                  <td className="hidden md:table-cell px-4 py-2" />
                  <td className="px-4 py-2 text-right">
                    <span className="font-bold text-purple-700 whitespace-nowrap">{formatCurrency(grupo.totalMonto)}</span>
                  </td>
                  {!readOnly && <td className="hidden md:table-cell px-4 py-2" />}
                </tr>
                {grupo.pedidos.map((pedido) => (
                  <PedidoRow key={pedido.id} pedido={pedido} {...rowProps} />
                ))}
              </Fragment>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
});
